/**
 * Every certificate this learner holds.
 *
 * Structure follows the prototype's `screen-my-certificates`: a page head, year filter
 * chips, then rows grouped by year — each a seal, a title, a meta line and a View button.
 *
 * WHAT THE PLATFORM WILL AND WILL NOT TELL US
 * -------------------------------------------
 * `/api/certificates/v0/certificates/<username>/` is the list, and it is already filtered
 * to what the learner may see: passing status, the course's display behaviour, and an ACTIVE
 * certificate configuration on the course. So a certificate that has been earned but is
 * being held back is not in this list at all — it is not returned as "pending", it is
 * absent.
 *
 * That absence is the whole reason this page also reads the dashboard endpoint. The
 * per-course certificate state there carries `isEarned` and `availableDate`, which is where
 * "you have earned this, it appears on <date>" lives. Without it a learner who has finished
 * a course sees an empty page and no explanation, which reads as a fault.
 *
 * TWO KINDS OF CREDENTIAL, ONE LIST — and they are told apart rather than blended.
 * The prototype showed pathway credentials here and, at the time this file was written,
 * there were none to show: listing a course certificate as though it were one would have
 * misrepresented what the academy had issued. `PathwayCertificate` rows exist now, so they
 * are merged in, date-sorted with the rest, each carrying a "Learning path" chip. They come
 * from a different table and a different endpoint; the chip is what stops a reader assuming
 * one row means the same as the next.
 *
 * Colour is entirely tokens, so the seal and accents are whatever the academy configured.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Banner, EmptyState, Icon, Spinner } from '../../shared/ui';
import { loadSession } from '../../shared/lib/session';
import { courseIdVisibleToTenant, getMyCourses } from '../../shared/lib/catalogue';
import {
  certificateViewUrl,
  formatIssued,
  gradePercent,
  listEarnedCertificates,
} from '../../shared/lib/certificates';
import {
  listMyPathwayCertificates,
  pathwayCertificateHref,
} from '../../shared/lib/pathway-certificates';
import './certificates.css';

/**
 * One row, whichever kind of credential produced it.
 *
 * Built here rather than rendered from two shapes because the page's job — group by year,
 * filter by year, count — is the same question for both, and asking it twice is how the
 * chips come to disagree with the rows they count.
 */
interface CertificateRow {
  id: string;
  kind: 'course' | 'pathway';
  title: string;
  meta: string[];
  /** An in-app route for both. Neither is a platform-rendered page any more. */
  href: string | null;
  /** ISO, for sorting and for the year heading. */
  issued: string;
}

function groupByYear(rows: CertificateRow[]): Array<[string, CertificateRow[]]> {
  const groups = new Map<string, CertificateRow[]>();
  for (const row of rows) {
    const parsed = Date.parse(row.issued);
    const year = Number.isNaN(parsed) ? 'Undated' : String(new Date(parsed).getFullYear());
    const bucket = groups.get(year);
    if (bucket) bucket.push(row);
    else groups.set(year, [row]);
  }
  return [...groups.entries()].sort((a, b) => b[0].localeCompare(a[0]));
}


export default function MyCertificates() {
  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: 5 * 60_000 });
  const username = session.data?.user?.username ?? null;

  const certificates = useQuery({
    queryKey: ['my-certificates', username],
    queryFn: () => listEarnedCertificates(username as string),
    enabled: Boolean(username),
  });

  // The dashboard read, only for the courses this page cannot list. Its own query so a slow
  // dashboard never holds up the certificates that are ready.
  const home = useQuery({ queryKey: ['my-courses'], queryFn: getMyCourses });

  // Pathway credentials. Its own query, and never fatal: an academy with no pathways — or a
  // pathways API that is briefly unavailable — must still show the course certificates this
  // page has always shown. The server has already scoped these to this academy.
  const pathways = useQuery({
    queryKey: ['my-pathway-certificates'],
    queryFn: listMyPathwayCertificates,
    enabled: Boolean(username),
    retry: false,
  });

  // Narrowed to this academy's certificates, by EITHER route a course can belong to it: its
  // org, or a per-course licence. Org alone would hide the certificate for a licensed course
  // the learner studied here — the one place that omission is unrecoverable, because a
  // certificate absent from this list has nowhere else to be found.
  const mine = useMemo(
    () =>
      (certificates.data ?? []).filter((entry) => courseIdVisibleToTenant(entry.course_id)),
    [certificates.data],
  );

  /**
   * Courses the learner has earned a certificate in that the list above does not carry.
   *
   * Matched on course key rather than on `isDownloadable`, because the two endpoints
   * disagree by design: the dashboard reports what has been earned, the certificates list
   * reports what may be shown. The difference is exactly what needs explaining.
   */
  const pending = useMemo(() => {
    const listed = new Set(mine.map((entry) => entry.course_id));
    return (home.data ?? []).filter(
      (entry) => entry.certificate.isEarned && !listed.has(entry.courseRun.courseId),
    );
  }, [home.data, mine]);

  /**
   * Both kinds, in one date-sorted list.
   *
   * Sorted newest first ACROSS the two sources rather than concatenated, because a learner
   * finishing a pathway usually earns its last course certificate seconds earlier — and a
   * list that put every pathway credential after every course one would separate the two
   * halves of the same afternoon.
   */
  const rows = useMemo<CertificateRow[]>(() => {
    const courseRows: CertificateRow[] = mine.map((entry) => {
      const grade = gradePercent(entry.grade);
      return {
        id: `course:${entry.uuid || entry.course_id}`,
        kind: 'course',
        title: entry.course_display_name,
        meta: [
          'Course',
          entry.course_organization,
          `Issued ${formatIssued(entry.created_date)}`,
          // The grade is only meaningful when the course graded something. An ungraded
          // course stores 0.0, and printing "0%" beside a certificate somebody earned reads
          // as a fault.
          grade ? `${grade}%` : null,
          entry.uuid ? `ID ${entry.uuid.slice(0, 8)}` : null,
        ].filter((value): value is string => Boolean(value)),
        href: certificateViewUrl(entry),
        issued: entry.created_date,
      };
    });

    const pathwayRows: CertificateRow[] = (pathways.data ?? []).map((entry) => ({
      id: `pathway:${entry.uuid}`,
      kind: 'pathway',
      title: entry.pathway.title,
      meta: [
        'Learning path',
        entry.pathway.academy_name,
        `Issued ${formatIssued(entry.issued)}`,
        `${entry.courses.length} ${entry.courses.length === 1 ? 'course' : 'courses'}`,
        `ID ${entry.uuid.slice(0, 8)}`,
      ].filter(Boolean),
      href: pathwayCertificateHref(entry.uuid),
      issued: entry.issued ?? '',
    }));

    return [...courseRows, ...pathwayRows].sort(
      (a, b) => (Date.parse(b.issued) || 0) - (Date.parse(a.issued) || 0),
    );
  }, [mine, pathways.data]);

  const groups = useMemo(() => groupByYear(rows), [rows]);
  const years = groups.map(([year]) => year);
  const [year, setYear] = useState<string | null>(null);
  const shown = year ? groups.filter(([group]) => group === year) : groups;

  if (session.isLoading || (username && certificates.isLoading)) {
    return (
      <main className="certs">
        <Spinner label="Loading your certificates…" />
      </main>
    );
  }

  if (!username) {
    return (
      <main className="certs">
        <Banner tone="info">
          <strong>Sign in to see your certificates.</strong> They are tied to your account,
          not to this browser.
        </Banner>
      </main>
    );
  }

  return (
    <main className="certs">
      <div className="certs__head">
        <div>
          <h1>Certificates</h1>
          <p>Everything you have earned in this academy.</p>
        </div>
        <Link className="ui-btn" data-variant="secondary" to="/u">
          <Icon name="arrow_back" />
          Back to profile
        </Link>
      </div>

      {certificates.isError && (
        <Banner tone="danger">
          <strong>Your certificates could not be loaded.</strong> Nothing has been lost —
          reload the page, and tell the academy if it keeps happening.
        </Banner>
      )}

      {/* Earned but not yet showable. Above the list on purpose: it is the state a learner
          who has just finished a course arrives here to ask about. */}
      {pending.length > 0 && (
        <Banner tone="info">
          <strong>
            {pending.length === 1
              ? 'One certificate is on its way.'
              : `${pending.length} certificates are on their way.`}
          </strong>{' '}
          {pending.map((entry) => entry.course.courseName).join(', ')} — earned, but the
          academy has not released it yet. Courses can be set to hold certificates back
          until they end; ask the academy when yours becomes available.
        </Banner>
      )}

      {rows.length === 0 ? (
        <EmptyState
          title="No certificates yet"
          body="Finish a course and its certificate appears here. If a course grades you, you need a passing grade as well."
          action={
            <Link className="ui-btn" data-variant="primary" to="/">
              Go to my courses
            </Link>
          }
        />
      ) : (
        <>
          {years.length > 1 && (
            <div className="certs__filters" role="tablist" aria-label="Filter by year">
              <button
                type="button"
                role="tab"
                aria-selected={year === null}
                className="certs__chip"
                data-active={year === null}
                onClick={() => setYear(null)}
              >
                All <span>({rows.length})</span>
              </button>
              {groups.map(([group, groupRows]) => (
                <button
                  type="button"
                  role="tab"
                  key={group}
                  aria-selected={year === group}
                  className="certs__chip"
                  data-active={year === group}
                  onClick={() => setYear(group)}
                >
                  {group} <span>({groupRows.length})</span>
                </button>
              ))}
            </div>
          )}

          {shown.map(([group, groupRows]) => (
            <section className="certs__group" key={group}>
              <h2>
                {group} <span>{groupRows.length}</span>
              </h2>
              <div className="certs__list">
                {groupRows.map((row) => (
                  <CertificateRowView key={row.id} row={row} />
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </main>
  );
}

/**
 * One row. The chip is the whole of the difference between the two kinds.
 *
 * `<Link>`, not `<a href>`: both certificate documents are routes in this app now. The
 * course one used to be the platform's server-rendered page and was navigated to with a
 * plain anchor; nothing proxies that path any more, so a full page load would only make the
 * app boot twice.
 */
function CertificateRowView({ row }: { row: CertificateRow }) {
  return (
    <article className="cert-row" data-kind={row.kind}>
      <span className="cert-row__seal" aria-hidden="true">
        <Icon name={row.kind === 'pathway' ? 'route' : 'workspace_premium'} />
      </span>
      <div className="cert-row__body">
        <h3>
          {row.title}
          {row.kind === 'pathway' && (
            <span className="cert-row__chip">
              <Icon name="route" />
              Learning path
            </span>
          )}
        </h3>
        <p>{row.meta.join(' · ')}</p>
      </div>
      {/* An absolute value can only be a legacy PDF held in external storage, which is not
          a route in this app — so it keeps a plain anchor. Everything issued here is
          relative and is a route. */}
      {!row.href ? (
        <span className="cert-row__note">No link available</span>
      ) : /^https?:/i.test(row.href) ? (
        <a className="ui-btn" data-variant="primary" href={row.href} target="_blank" rel="noreferrer">
          View
        </a>
      ) : (
        <Link className="ui-btn" data-variant="primary" to={row.href}>
          View
        </Link>
      )}
    </article>
  );
}
